import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import axiosInstance from './Interceptor';
import { v4 as uuidv4 } from 'uuid'; // Import uuid for session ID generation

// Function to send UTM and booking data to Google Sheet
export const sendUTMToGoogleSheet = async (
  metaData = {},
  dispatch,
  bookingData = {}
) => {
  const log = process.env.NODE_ENV === 'development' ? console.log : () => {};

  // Retrieve UTM parameters and session ID from sessionStorage
  if(!sessionStorage.getItem('utm_medium')) return;

  const utmParams = {
    utm_campaign: sessionStorage.getItem('utm_campaign') || '',
    utm_adgroup: sessionStorage.getItem('utm_adgroup') || '',
    utm_medium: sessionStorage.getItem('utm_medium') || '',
    utm_source: sessionStorage.getItem('utm_source') || '',
    utm_term: sessionStorage.getItem('utm_term') || '',
    session_id: sessionStorage.getItem('session_id') || ''
  };

  log('Sending to /api/user/cms-meta:', {
    utmParams,
    metaData,
    bookingData,
    timestamp: new Date().toISOString()
  });

  try {
    // dispatch(setLoader(true));
    const { data } = await axiosInstance.post(
      '/api/user/cms-meta',
      {
        utm_campaign: utmParams.utm_campaign,
        utm_adgroup: utmParams.utm_adgroup,
        utm_medium: utmParams.utm_medium,
        utm_source: utmParams.utm_source,
        utm_term: utmParams.utm_term,
        page: window.location.pathname || '',
        referrer: document.referrer || '',
        meta_title: metaData.title || '',
        meta_description: metaData.meta_description || '',
        meta_keywords: metaData.meta_keywords || '',
        session_id: utmParams.session_id,
        username: bookingData.username || '',
        booking_id: bookingData.booking_id || '',
        booking_type: bookingData.booking_type || '',
        user_phone_no: bookingData.user_phone_no || '',
        email: bookingData.email || '',
        date_time: bookingData.date_time || '',
        test_package_name: bookingData.test_package_name || '',
        amount_of_purchase: bookingData.amount_of_purchase || ''
      },
      {
        headers: { 'Content-Type': 'application/json' },
        timeout: 5000
      }
    );

    log('Data sent to Google Sheet:', {
      response: data,
      timestamp: new Date().toISOString()
    });
    return data;
  } catch (error) {
    console.error('Error sending data:', error.message, {
      timestamp: new Date().toISOString()
    });
    throw error;
  } finally {
    // dispatch(setLoader(false));
  }
};

const UTMTracker = ({ bookingData = {} }) => {
  const dispatch = useDispatch();
  const hasSentInitialUTM = useRef(false); // Track initial UTM send

  console.log('UTMTracker mounted:', {
    timestamp: new Date().toISOString(),
    pathname: window.location.pathname
  });

  // Store UTM parameters and session ID in sessionStorage
  const storeUTMParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (
      urlParams.has('utm_campaign') ||
      urlParams.has('utm_adgroup') ||
      urlParams.has('utm_medium') ||
      urlParams.has('utm_source') ||
      urlParams.has('utm_term')
    ) {
      sessionStorage.setItem(
        'utm_campaign',
        urlParams.get('utm_campaign') || ''
      );
      sessionStorage.setItem('utm_adgroup', urlParams.get('utm_adgroup') || '');
      sessionStorage.setItem('utm_medium', urlParams.get('utm_medium') || '');
      sessionStorage.setItem('utm_source', urlParams.get('utm_source') || '');
      sessionStorage.setItem('utm_term', urlParams.get('utm_term') || '');
      // Generate and store session ID if not already present
      if (!sessionStorage.getItem('session_id')) {
        sessionStorage.setItem('session_id', uuidv4());
      }
      console.log('UTM parameters stored in sessionStorage:', {
        utmParams: {
          utm_campaign: urlParams.get('utm_campaign') || '',
          utm_adgroup: urlParams.get('utm_adgroup') || '',
          utm_medium: urlParams.get('utm_medium') || '',
          utm_source: urlParams.get('utm_source') || '',
          utm_term: urlParams.get('utm_term') || '',
          session_id: sessionStorage.getItem('session_id')
        },
        timestamp: new Date().toISOString()
      });
    }
  };

  // Handle UTM and booking data submission
  const fetchMetaDataAndSend = async () => {
    // Check if this is a booking submission (any booking field present)
    const isBookingSubmission =
      bookingData.username ||
      bookingData.booking_id ||
      bookingData.booking_type ||
      bookingData.user_phone_no;

    // For booking submissions, send data without checking utmSent
    if (isBookingSubmission) {
      try {
        // Fetch meta data from admin API (optional)
        let metaData = {};
        try {
          const { data } = await axiosInstance.get('/api/user/cms-meta', {
            headers: { 'Content-Type': 'application/json' },
            timeout: 5000
          });
          metaData = data.data || {};
          console.log('Meta data fetched for booking:', {
            metaData,
            timestamp: new Date().toISOString()
          });
        } catch (error) {
          console.error(
            'Error fetching meta data for booking:',
            error.message,
            { timestamp: new Date().toISOString() }
          );
        }
        await sendUTMToGoogleSheet(metaData, dispatch, bookingData);
        console.log('Booking data sent:', {
          bookingData,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error in booking data tracking:', error.message, {
          timestamp: new Date().toISOString()
        });
      }
      return;
    }

    // For non-booking data, only send if UTM hasn't been sent and parameters exist
    if (sessionStorage.getItem('utmSent') || hasSentInitialUTM.current) {
      console.log('UTM data already sent in this session, skipping...', {
        timestamp: new Date().toISOString()
      });
      return;
    }

    if (
      !sessionStorage.getItem('utm_campaign') &&
      !sessionStorage.getItem('utm_adgroup') &&
      !sessionStorage.getItem('utm_medium') &&
      !sessionStorage.getItem('utm_source') &&
      !sessionStorage.getItem('utm_term')
    ) {
      console.log('No UTM data available, skipping...', {
        timestamp: new Date().toISOString()
      });
      return;
    }

    try {
      // Set utmSent immediately to prevent race conditions
      sessionStorage.setItem('utmSent', 'true');
      hasSentInitialUTM.current = true;

      // Fetch meta data from admin API (optional)
      let metaData = {};
      try {
        const { data } = await axiosInstance.get('/api/user/cms-meta', {
          headers: { 'Content-Type': 'application/json' },
          timeout: 5000
        });
        metaData = data.data || {};
        console.log('Meta data fetched for UTM:', {
          metaData,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error fetching meta data for UTM:', error.message, {
          timestamp: new Date().toISOString()
        });
      }
      await sendUTMToGoogleSheet(metaData, dispatch, {});
      console.log('UTM data sent and marked in sessionStorage:', {
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error in UTM tracking:', error.message, {
        timestamp: new Date().toISOString()
      });
      // Revert utmSent on failure to allow retry
      sessionStorage.removeItem('utmSent');
      hasSentInitialUTM.current = false;
    }
  };

  useEffect(() => {
    console.log('UTMTracker useEffect triggered:', {
      bookingData,
      timestamp: new Date().toISOString()
    });
    // Always store UTM parameters on mount
    storeUTMParams();
    // Send data only if it's a booking submission or initial UTM send
    fetchMetaDataAndSend();
  }, [bookingData]); // Re-run if bookingData changes

  return null;
};

export default UTMTracker;
